import { isNull } from 'lodash/fp';

import { ImageType } from '../../../types/image';
import imageUrlBuilder from '../image-url-builder';
import type { ImageObject, Maybe } from '../../../types/__codegen__/sanity/graphql';

type GetImageAttributesProps = {
  height?: number;
  imageObject?: Maybe<ImageObject>;
  width?: number;
};

const getDimension = (assetDimension?: number | null, dimension?: number) => {
  if (!dimension || !assetDimension) {
    return dimension;
  }
  return Math.min(assetDimension, dimension);
};

const getImageAttributes = ({ height, imageObject, width }: GetImageAttributesProps): ImageType | null => {
  const image = imageObject?.image;

  const assetDimensions = imageObject?.image?.asset?.metadata?.dimensions;
  const assetHeight = assetDimensions?.height;
  const assetWidth = assetDimensions?.width;

  if (!image?.asset) {
    return null;
  }

  const hasAssetDimensions = !!assetWidth && assetWidth > 0 && !!assetHeight && assetHeight > 0;

  const caption = imageObject?.caption ?? '';
  const urlBuilder = imageUrlBuilder(image)
    .height(getDimension(assetHeight, height)!)
    .width(getDimension(assetWidth, width)!)
    .dpr(2); // request 2x for retina screens and avoiding rehydration issues

  const lqip = image.asset.metadata?.lqip;

  const imageMetadata: Partial<ImageType> = hasAssetDimensions
    ? {
        ...(lqip
          ? {
              blurDataURL: lqip,
              placeholder: 'blur',
            }
          : {}),
        height: assetHeight,
        unoptimized: true, // Sanity CDN will optimise already, don't re-compress.
        useNextImage: true,
        width: assetWidth,
      }
    : {};

  return {
    ...imageMetadata,
    alt: isNull(caption) ? '' : caption,
    src: urlBuilder.url()!,
  };
};

export default getImageAttributes;
